import { InjectionToken, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Error thrown by the Web APIs DI tokens when the global scope cannot be found.
 */
class UnavailableWindowError extends Error {
    constructor(message) {
        super(message ?? 'Window is not available');
    }
}

function getDescription(token) {
    const value = token.toString();
    return value.substr(value.indexOf(' ') + 1);
}
/**
 * Creates a new DI token that when the UnavailableWindowError error is thrown, it will resolve to `null` instead of throwing.
 * @param token a Web API DI token to wrap.
 * @returns a new DI token.
 *
 * @example
 * ```typescript
 * const OPTIONAL_WINDOW = asOptionalWebApiToken(WINDOW);
 *
 * const window = inject(OPTIONAL_WINDOW); // `null` if window cannot be resolved.
 * ```
 */
function asOptionalWebApiToken(token) {
    const description = getDescription(token);
    return new InjectionToken(`[optional] ${description}`, {
        factory() {
            try {
                return inject(token);
            }
            catch (e) {
                if (e instanceof UnavailableWindowError)
                    return null;
                throw e;
            }
        },
    });
}

/**
 * A DI Token representing the global scope. In a browser this is the Window object.
 *
 * @remarks
 * This token will throw an UnavailableWindowError error if the global scope (window) cannot be found.
 */
const WINDOW = new InjectionToken('@slidebean/hydra/web-apis/window', {
    factory() {
        const { defaultView } = inject(DOCUMENT);
        if (!defaultView) {
            throw new UnavailableWindowError();
        }
        return defaultView;
    },
});
/**
 * A DI Token representing the global scope. In a browser this is the Window object.
 *
 * @remarks
 * This token will resolve to null if the global scope (window) cannot be found.
 */
const OPT_WINDOW = asOptionalWebApiToken(WINDOW);

/**
 * A DI Token representing the navigator object found in the global scope.
 *
 * @remarks
 * This token will throw an UnavailableWindowError error if the global scope (window) cannot be found.
 */
const NAVIGATOR = new InjectionToken('@slidebean/hydra/web-apis/navigator', {
    factory() {
        return inject(WINDOW).navigator;
    },
});
/**
 * A DI Token representing the navigator object found in the global scope.
 *
 * @remarks
 * This token will resolve to null if the global scope (window) cannot be found.
 */
const OPT_NAVIGATOR = asOptionalWebApiToken(WINDOW);

/**
 * A DI Token representing the user agent of the navigator object.
 *
 * @remarks
 * This token will throw an UnavailableWindowError error if the global scope (window) cannot be found.
 */
const USER_AGENT = new InjectionToken('@slidebean/hydra/web-apis/user-agent', {
    factory() {
        return inject(NAVIGATOR).userAgent;
    },
});
/**
 * A DI Token representing the user agent of the navigator object.
 *
 * @remarks
 * This token will resolve to null if the global scope (window) cannot be found.
 */
const OPT_USER_AGENT = asOptionalWebApiToken(USER_AGENT);

/**
 * Generated bundle index. Do not edit.
 */

export { NAVIGATOR, OPT_NAVIGATOR, OPT_USER_AGENT, OPT_WINDOW, USER_AGENT, UnavailableWindowError, WINDOW, asOptionalWebApiToken };
